/**
 * EnvironmentalConditionsSection - Environmental conditions for incident reporting
 * Includes weather, sea state (Douglas Scale), and wind force (Beaufort Scale)
 */

import React from 'react';
import Dropdown from '../../../../common/Dropdown';

const EnvironmentalConditionsSection = ({ formData, handleInputChange, errors }) => {
  // Weather conditions options
  const weatherOptions = [
    { value: 'clear', label: 'Clear' },
    { value: 'partly_cloudy', label: 'Partly Cloudy' },
    { value: 'cloudy', label: 'Cloudy' },
    { value: 'rain_light', label: 'Light Rain' },
    { value: 'rain_heavy', label: 'Heavy Rain' },
    { value: 'drizzle', label: 'Drizzle' },
    { value: 'fog', label: 'Fog' },
    { value: 'mist', label: 'Mist' },
    { value: 'snow', label: 'Snow' },
    { value: 'sleet', label: 'Sleet' },
    { value: 'hail', label: 'Hail' },
    { value: 'thunderstorm', label: 'Thunderstorm' },
    { value: 'gale', label: 'Gale' }
  ];

  // Sea state options (Douglas Sea Scale)
  const seaStateOptions = [
    { value: '0', label: '0 - Calm (glassy)' },
    { value: '1', label: '1 - Calm (rippled)' },
    { value: '2', label: '2 - Smooth (wavelets)' },
    { value: '3', label: '3 - Slight (0.5-1.25m)' },
    { value: '4', label: '4 - Moderate (1.25-2.5m)' },
    { value: '5', label: '5 - Rough (2.5-4m)' },
    { value: '6', label: '6 - Very rough (4-6m)' },
    { value: '7', label: '7 - High (6-9m)' },
    { value: '8', label: '8 - Very high (9-14m)' },
    { value: '9', label: '9 - Phenomenal (>14m)' }
  ];

  // Wind force options (Beaufort Scale)
  const windForceOptions = [
    { value: '0', label: '0 - Calm (<1 knot)' },
    { value: '1', label: '1 - Light air (1-3 knots)' },
    { value: '2', label: '2 - Light breeze (4-6 knots)' },
    { value: '3', label: '3 - Gentle breeze (7-10 knots)' },
    { value: '4', label: '4 - Moderate breeze (11-16 knots)' },
    { value: '5', label: '5 - Fresh breeze (17-21 knots)' },
    { value: '6', label: '6 - Strong breeze (22-27 knots)' },
    { value: '7', label: '7 - Near gale (28-33 knots)' },
    { value: '8', label: '8 - Gale (34-40 knots)' },
    { value: '9', label: '9 - Strong gale (41-47 knots)' },
    { value: '10', label: '10 - Storm (48-55 knots)' },
    { value: '11', label: '11 - Violent storm (56-63 knots)' },
    { value: '12', label: '12 - Hurricane force (≥64 knots)' }
  ];

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">
        Environmental Conditions
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Weather Conditions
          </label>
          <Dropdown
            value={formData.weatherConditions}
            onChange={(val) => handleInputChange('weatherConditions', val)}
            options={weatherOptions}
            placeholder="Select weather conditions..."
            width="w-full"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Visibility
          </label>
          <Dropdown
            value={formData.visibility}
            onChange={(val) => handleInputChange('visibility', val)}
            options={[
              { value: 'good', label: 'Good (>5 miles)' },
              { value: 'moderate', label: 'Moderate (1-5 miles)' },
              { value: 'poor', label: 'Poor (<1 mile)' },
              { value: 'fog', label: 'Fog (<0.5 mile)' }
            ]}
            width="w-full"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sea State (Douglas Scale)
          </label>
          <Dropdown
            value={formData.seaState}
            onChange={(val) => handleInputChange('seaState', val)}
            options={seaStateOptions}
            placeholder="Select sea state..."
            width="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Wind Force (Beaufort Scale)
          </label>
          <Dropdown
            value={formData.windForce || ''}
            onChange={(val) => handleInputChange('windForce', val)}
            options={windForceOptions}
            placeholder="Select wind force..."
            width="w-full"
          />
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalConditionsSection;